import React, { useEffect, useMemo, useCallback, useState } from "react";
import { Layout, theme, Grid, Button, Tooltip } from "antd";
import { Outlet } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useAppContext } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import {
  getThemeSettings,
  getStoredThemeSettings,
  saveStoredThemeSettings,
} from "../services/themeService";
import { permissionService } from "../services/permissionService";
import RooftopNavigation from "../navigation/RooftopNavigationContainer";
import PageLoader from "../components/PageLoader";
import { LogoutOutlined } from "@ant-design/icons";

const { useBreakpoint } = Grid;

const RooftopLayoutInner: React.FC = () => {
  const {
    state: { isNavMenuClose },
  } = useAppContext();
  const { theme: currentTheme, updateTheme } = useTheme();
  const { user, logout } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const isDarkMode = currentTheme.algorithm?.[0] === theme.darkAlgorithm;
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [partyName, setPartyName] = useState<string>("");

  useEffect(() => {
    const storedPartyName = localStorage.getItem("selectedPartyName");
    if (storedPartyName) {
      setPartyName(storedPartyName);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyName") {
        setPartyName(event.newValue || "");
      }     
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const themeTokens = useMemo(
    () => ({
      background: isDarkMode ? "#141414" : "#f5f7fa",
      contentBackground: isDarkMode ? "#1f1f1f" : "#ffffff",
      textColor: isDarkMode
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
      textColorSecondary: isDarkMode
        ? "rgba(255, 255, 255, 0.45)"
        : "rgba(0, 0, 0, 0.45)",
      borderColor: isDarkMode ? "#303030" : "#f0f0f0",
      primaryColor: currentTheme.token?.colorPrimary || "#1d8348",
    }),
    [isDarkMode, currentTheme.token?.colorPrimary]
  );

  const loadThemeFromAPI = useCallback(async () => {
    try {
      if (getStoredThemeSettings()) {
        return;
      }

      const apiSettings = await getThemeSettings();
      if (apiSettings) {
        saveStoredThemeSettings(apiSettings);
        updateTheme(apiSettings);
      }
    } catch (error) {
      console.error("Failed to load theme settings in MainLayout:", error);
    }
  }, [updateTheme]);

  const loadStagePermissions = useCallback(async () => {
    const partyId = localStorage.getItem("selectedPartyId");
    const stageId = localStorage.getItem("selectedStageId");
    if (!partyId || !stageId || !user?.id) {
      console.debug(
        "Cannot load stage permissions: no party or stage selected"
      );
      setPermissionsLoading(false);
      return;
    }

    try {
      setPermissionsLoading(true);
      await permissionService.refreshStagePermissions(
        stageId,
        user.id || "",
        partyId
      );
    } catch (error) {
      console.error("Failed to load stage permissions in ClientLayout:", error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!getStoredThemeSettings()) {
      loadThemeFromAPI();
    }
  }, [loadThemeFromAPI]);

  useEffect(() => {
    if (!user?.isclient) {
      loadStagePermissions();
    }
  }, [loadStagePermissions, user?.isclient]);

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "selectedPartyId") {
        loadStagePermissions();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [loadStagePermissions]);

  const layoutStyles = useMemo(
    () => ({
      backgroundColor: themeTokens.background,
      minHeight: "100vh",
      height: "100%",
      overflow: "hidden",
      display: "flex",
      color: themeTokens.textColor,
      transition: "all 0.3s ease-in-out",
    }),
    [themeTokens]
  );

  const contentStyles = useMemo(
    () => ({
      marginLeft: isMobile ? 0 : isNavMenuClose ? 80 : 240,
      width: "100%",
      padding: isMobile ? "56px 16px 16px" : "0",
      overflow: "auto",
      minHeight: "100vh",
      background: themeTokens.contentBackground,
      transition: "all 0.3s ease-in-out",
      color: themeTokens.textColor,
    }),
    [themeTokens, isNavMenuClose, isMobile]
  );

  const headerStyles = useMemo(
    () => ({
      background: "transparent",
      padding: isMobile ? "8px 12px" : "8px 16px",
      height: 52,
      lineHeight: "52px",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end" as const,
      color: themeTokens.textColor,
      backdropFilter: "none",
    }),
    [isMobile, themeTokens.textColor]
  );

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      isDarkMode ? "dark" : "light"
    );
    document.body.style.backgroundColor = themeTokens.background;
    document.body.style.color = themeTokens.textColor;

    const style = document.createElement("style");
    style.textContent = `
      :root {
        color-scheme: ${isDarkMode ? "dark" : "light"};
        --primary-color: ${themeTokens.primaryColor};
        --background-color: ${themeTokens.background};
        --content-background: ${themeTokens.contentBackground};
        --text-color: ${themeTokens.textColor};
        --text-color-secondary: ${themeTokens.textColorSecondary};
        --border-color: ${themeTokens.borderColor};
      }

      @keyframes insuranceGlow {
        from {
          text-shadow: 0 0 4px rgba(82, 196, 26, 0.4), 0 0 10px rgba(82, 196, 26, 0.2);
        }
        to {
          text-shadow: 0 0 10px rgba(82, 196, 26, 0.8), 0 0 20px rgba(82, 196, 26, 0.4);
        }
      }

      .insurance-expiry-date {
        color: #52c41a !important;
        animation: insuranceGlow 3s ease-in-out infinite alternate;
        transition: color 0.3s ease;
        font-weight: 700;
      }

    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [isDarkMode, themeTokens]);

  return (
    <Layout style={layoutStyles}>
      <RooftopNavigation />
      <Layout style={contentStyles}>
        <Layout.Header style={headerStyles}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "space-between",
            width: "100%"
          }}>
            {/* Left side - Party Name */}
            <div style={{ 
              fontWeight: 600, 
              fontSize: isMobile ? "16px" : "23px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {partyName || ""}
            </div>
            
            {/* Right side - Insurance, Notifications, Logout, and Username */}
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 6 : 12 }}>              
              {/* Notifications, Logout, Username */}
              {/* Notifications icon hidden */}
              {/* <Tooltip title="Notifications">
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<BellOutlined />}
                    onClick={handleNotificationClick}
                  />
                </Badge>
              </Tooltip> */}
              <Tooltip title="Logout">
                <Button
                  type="text"
                  shape="circle"
                  size="small"
                  icon={<LogoutOutlined />}
                  onClick={logout}
                />
              </Tooltip>
              <Tooltip title={user?.email || ""}>
                <div style={{ 
                  fontWeight: 600, 
                  cursor: "default",
                  fontSize: isMobile ? "14px" : "23px"
                }}>
                  {user?.name || "User"}
                </div>
              </Tooltip>
            </div>
          </div>
        </Layout.Header>
        <Layout.Content>
          {permissionsLoading ? <PageLoader /> : <Outlet />}
        </Layout.Content>
      </Layout>

      {/* Notification Panel - disabled */}
      {/* <NotificationPanel
        visible={notificationPanelVisible}
        onClose={handleNotificationPanelClose}
      /> */}
    </Layout>
  );
};

const RooftopLayout: React.FC = () => {
  return (
    <RooftopLayoutInner />
  );
};

export default React.memo(RooftopLayout);
